import type { AccountingBackend } from "../../config/accounting";

/** True when value is exactly 1 — OCR/Zoho placeholder, not a real FX rate for foreign docs. */
export function isImplicitDefaultExchangeRate(value: unknown): boolean {
  const raw = String(value ?? "").trim();
  if (raw === "") return false;
  const rate = Number(raw);
  return Number.isFinite(rate) && Math.abs(rate - 1) < 1e-9;
}

export type ExchangeRatePostContext = {
  /** User explicitly saved the rate from Basic Info edit. */
  userSet?: boolean;
  /** Document already posted to accounting — keep stored rate (incl. legacy 1). */
  isPosted?: boolean;
};

/**
 * Tally: foreign-currency docs need a rate before post.
 * Implicit 1 without user confirmation counts as missing (typical OCR default).
 */
export function isExchangeRateMissingForTallyPost(
  rate: unknown,
  ctx: ExchangeRatePostContext,
): boolean {
  if (rate == null || rate === "") return true;
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return true;
  if (isImplicitDefaultExchangeRate(n) && !ctx.userSet && !ctx.isPosted) {
    return true;
  }
  return false;
}

/** Zoho: strip OCR/default 1 unless the user explicitly saved it. */
export function shouldStripImplicitExchangeRateForZoho(
  rate: unknown,
  ctx: ExchangeRatePostContext,
): boolean {
  if (!isImplicitDefaultExchangeRate(rate)) return false;
  if (ctx.userSet) return false;
  if (ctx.isPosted) return false;
  return true;
}

/**
 * Normalize rate for API payload.
 * Zoho: null when unset or implicit 1; explicit user 1 stays 1.
 * Tally: returns numeric rate unchanged (caller validates first).
 */
export function normalizeExchangeRateForPostPayload(
  rate: unknown,
  backend: AccountingBackend,
  ctx: ExchangeRatePostContext,
): number | null {
  if (rate == null || rate === "") return null;
  const n = Number(rate);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (backend === "zoho" && shouldStripImplicitExchangeRateForZoho(rate, ctx)) {
    return null;
  }
  return n;
}

/** Value shown in Basic Info exchange-rate input / read-only field. */
export function formatExchangeRateForBasicInfoField(
  rate: unknown,
  opts: {
    showFx: boolean;
    backend: AccountingBackend;
    userSet?: boolean;
  },
): string {
  if (!opts.showFx) return "";
  if (rate === undefined || rate === null || String(rate).trim() === "") {
    return "";
  }
  if (
    opts.backend === "zoho" &&
    isImplicitDefaultExchangeRate(rate) &&
    !opts.userSet
  ) {
    return "";
  }
  return String(rate);
}

/**
 * Zoho foreign-currency docs: fetch from Books when the stored rate is empty,
 * null, non-positive, or the OCR/default placeholder of 1.
 */
export function needsZohoExchangeRateFetch(rate: unknown): boolean {
  if (rate == null) return true;
  const raw = String(rate).trim();
  if (raw === "") return true;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return true;
  return isImplicitDefaultExchangeRate(n);
}

export function getExchangeRateDisplayText(
  exchangeRateRaw: string,
  opts: {
    showFx: boolean;
    backend: AccountingBackend;
    userSet?: boolean;
    loading?: boolean;
  },
): string {
  const trimmed = exchangeRateRaw.trim();
  if (!opts.showFx) return trimmed;

  if (opts.loading) {
    return "Fetching rate from Zoho…";
  }

  if (opts.backend === "tally" && trimmed === "") {
    return "Not set — required before posting to Tally";
  }

  const n = trimmed === "" ? Number.NaN : parseFloat(trimmed);
  const showZohoDefaultHint =
    opts.backend === "zoho" &&
    (trimmed === "" ||
      (Number.isFinite(n) &&
        isImplicitDefaultExchangeRate(n) &&
        !opts.userSet));

  if (showZohoDefaultHint) {
    return "Latest exchange rate will be used if not set.";
  }

  return trimmed;
}

export function applyExchangeRateForAccountingPayload<
  T extends {
    exchange_rate?: number | null;
    exchange_rate_user_set?: boolean;
  },
>(doc: T, backend: AccountingBackend, ctx: ExchangeRatePostContext): T {
  if (backend !== "zoho") {
    return doc;
  }
  return {
    ...doc,
    exchange_rate: normalizeExchangeRateForPostPayload(
      doc.exchange_rate,
      backend,
      ctx,
    ),
  };
}
